import {
  collection,
  DocumentData,
  getDocs,
  Query,
  query,
  QueryDocumentSnapshot,
  QuerySnapshot,
  where,
} from "firebase/firestore";
import { Artist, Collections, Guide, Song } from "../../types/type";
import { db } from "../../firebase/firebaseConfig";
import { stringUtils } from "./generate";
import { Gender, GuideSearchResult } from "../../hooks/search/useGuideSearch";

// 문서 데이터를 T 타입으로 매핑하는 함수
const mapDocToData = <T>(doc: QueryDocumentSnapshot<DocumentData>): T => {
  return { id: doc.id, ...doc.data() } as T;
};

const findExactMatchArtist = (
  artistName: string,
  artistList: Artist[]
): Artist | null => {
  const normalizedName = stringUtils.removeAllSpaces(artistName);
  return (
    artistList.find(
      (artist) =>
        stringUtils.removeAllSpaces(artist.artistName) === normalizedName
    ) || null
  );
};

const splitSearchQuery = (
  query: string,
  totalArtistList: Artist[]
): {
  artistName: string | null;
  songQuery: string | null;
} => {
  const cleanedQuery = stringUtils.cleanText(query);

  for (const artist of totalArtistList) {
    const artistNameNoSpace = stringUtils.cleanText(artist.artistName);

    if (cleanedQuery.startsWith(artistNameNoSpace)) {
      // 시작 부분 매칭: 매칭된 부분 이후의 문자열을 가져옴
      const remainingPart = cleanedQuery.slice(artistNameNoSpace.length);
      console.log({
        artistName: artist.artistName,
        songQuery: remainingPart || null,
      });
      return {
        artistName: artist.artistName,
        songQuery: remainingPart || null,
      };
    } else if (cleanedQuery.endsWith(artistNameNoSpace)) {
      // 끝 부분 매칭: 매칭된 부분 이전의 문자열을 가져옴

      const remainingPart = cleanedQuery.slice(0, -artistNameNoSpace.length);
      console.log({
        artistName: artist.artistName,
        songQuery: remainingPart || null,
      });
      return {
        artistName: artist.artistName,
        songQuery: remainingPart || null,
      };
    }
  }

  return {
    artistName: null,
    songQuery: cleanedQuery,
  };
};

export const searchFirestore = async (
  searchQuery: string,
  totalArtistList: Artist[]
): Promise<{ songList: Song[]; artistList: Artist[] }> => {
  const { artistName, songQuery } = splitSearchQuery(
    searchQuery,
    totalArtistList
  );

  // if (stringUtils.isSingleKoreanCharacter(searchQuery)) {
  //   return { songList: [], artistList: [] };
  // }
  console.log("songQuery : ", songQuery);
  console.log("artistName : ", artistName);
  try {
    let songList: Song[] = [];
    let artistList: Artist[] = [];
    // 정확히 매칭되는 아티스트가 있는 경우
    if (artistName) {
      const exactMatchArtist = findExactMatchArtist(
        artistName,
        totalArtistList
      );
      if (exactMatchArtist) {
        if (songQuery) {
          const songsRef = collection(db, Collections.SONG);
          const songSearchQuery = query(
            songsRef,
            where("searchKeywordList", "array-contains", songQuery)
          );
          const songsSnapshot = await getDocs(songSearchQuery);
          songList = songsSnapshot.docs
            .map((doc) => mapDocToData<Song>(doc))
            .filter((song) =>
              song.artistIdList.includes(exactMatchArtist.artistId)
            );
        } else {
          const artistSongsRef = collection(db, Collections.SONG);
          const artistSongsQuery = query(
            artistSongsRef,
            where("artistIdList", "array-contains", exactMatchArtist.artistId)
          );
          const artistSongsSnapshot = await getDocs(artistSongsQuery);
          songList = artistSongsSnapshot.docs.map((doc) =>
            mapDocToData<Song>(doc)
          );
        }
        artistList = [exactMatchArtist];
      }
    } else {
      // 아티스트 매칭이 없는 경우, 기존 검색 로직 수행
      const songsRef = collection(db, Collections.SONG);
      const firestoreSongQuery = query(
        songsRef,
        where("searchKeywordList", "array-contains", songQuery)
      );
      const artistsRef = collection(db, Collections.ARTIST);
      const artistQuery = query(
        artistsRef,
        where("searchKeywordList", "array-contains", songQuery)
      );
      const [songSnapshot, artistSnapshot] = await Promise.all([
        getDocs(firestoreSongQuery),
        getDocs(artistQuery),
      ]);
      songList = songSnapshot.docs.map((doc) => mapDocToData<Song>(doc));
      artistList = artistSnapshot.docs.map((doc) => mapDocToData<Artist>(doc));
    }
    return { songList, artistList };
  } catch (error) {
    console.error("Firestore search error:", error);
    throw error;
  }
};

// Firestore 검색 함수
export const searchGuideFirestore = async (
  searchQuery: string,
  gender: Gender
): Promise<GuideSearchResult> => {
  try {
    const normalizedQuery = stringUtils.cleanText(searchQuery);
    const guideRef = collection(db, Collections.GUIDE);
    let guideQuery;

    // 'all'인 경우 성별 필터링 없이 검색
    if (gender === "all") {
      guideQuery = query(
        guideRef,
        where("searchKeywordList", "array-contains", normalizedQuery)
      );
    } else {
      guideQuery = query(
        guideRef,
        where("searchKeywordList", "array-contains", normalizedQuery),
        where("isMale", "==", gender === "male")
      );
    }

    const guideSnapshot = await getDocs(guideQuery);
    const guideList = guideSnapshot.docs.map((doc) => mapDocToData<Guide>(doc));

    return { guideList };
  } catch (error) {
    console.error("Firestore guide search error:", error);
    throw error;
  }
};
